import { KPI_TILE_COLORS } from "../kpiTileColors";

export type IntegrationCellStatus = "completed" | "not-completed" | "not-applicable";

export type IntegrationPlatformProgress = {
  status: IntegrationCellStatus;
  /** Pre-formatted label from API, e.g. "0/2 Completed" */
  displayLabel?: string;
  completedCount?: number;
  totalCount?: number;
};

export type IntegrationPlatformProgressRow = Record<string, IntegrationPlatformProgress>;

export const NOT_APPLICABLE_PROGRESS: IntegrationPlatformProgress = {
  status: "not-applicable",
};

export type IntegrationSummaryTile = {
  id: string;
  label: string;
  value: number;
  color: string;
};

export type IntegrationPlatformCard = {
  id: string;
  name: string;
  integrationCount: number;
  completionPercent: number;
  done: number;
  active: number;
  hold: number;
};

export type IntegrationPartnerRow = {
  id: string;
  /** Numeric partner DB ID from API `partner_id` (used for routing). */
  partnerId: number;
  partnerName: string;
  platformProgress: IntegrationPlatformProgressRow;
  expandedDetails?: IntegrationPlatformDetail[];
};

export type IntegrationMilestoneState =
  | "completed"
  | "in-progress"
  | "on-hold"
  | "pending";

export type IntegrationMilestone = {
  label: string;
  state: IntegrationMilestoneState;
  completedDate?: string;
  statusDate?: string;
  /** Shown instead of the pending label, e.g. "Not Applicable". */
  statusLabel?: string;
};

export type IntegrationCurrentStatus =
  | "completed"
  | "in-progress"
  | "on-hold"
  | "not-started"
  | "not-applicable"
  | "stage-completed";

export type IntegrationEndorsement = {
  label: string;
  variant: "success" | "danger" | "neutral";
};

export type IntegrationProjectDetail = {
  serviceName: string;
  provisioningType: string;
  technicalConsultant?: string;
  milestones: IntegrationMilestone[];
  currentStatus: IntegrationCurrentStatus;
  currentStatusSubTask?: string;
  age: string;
  validationStatus: IntegrationCellStatus;
  endorsement: IntegrationEndorsement;
  score: number;
};

export type IntegrationPlatformDetail = {
  /** Unique key for this technology section (slug of API technology name) */
  platformKey: string;
  /** Table column key used to show/hide when columns are toggled */
  columnKey: string;
  /** API `technology` field, e.g. "Apple Wallet - Corporate" */
  platformLabel: string;
  totalProjects: number;
  projects: IntegrationProjectDetail[];
};

export const INTEGRATION_SUMMARY_TILE_CONFIG: Omit<IntegrationSummaryTile, "value">[] = [
  { id: "overall", label: "Overall Completed", color: KPI_TILE_COLORS.green },
  { id: "completed-year", label: "Completed This Year", color: KPI_TILE_COLORS.green },
  { id: "in-progress", label: "In Progress", color: KPI_TILE_COLORS.orange },
  { id: "on-hold", label: "On-hold", color: KPI_TILE_COLORS.red },
];

/** Maps summary tile id to API `filters.validation_status` value (null = clear filter). */
export const INTEGRATION_SUMMARY_TILE_FILTER_STATUS: Record<
  string,
  string | null
> = {
  overall: null,
  "completed-year": "Completed This Year",
  "in-progress": "In Progress",
  "on-hold": "On Hold",
};
